/**
 * Entitlements Config Utility
 * ============================
 * Fetches canonical entitlements configuration from backend.
 * Single source of truth for lounge/speed dating minutes.
 * 
 * Usage:
 *   import { fetchEntitlementsConfig, perDayLabel, perMonthLabel } from '../utils/entitlementsConfig';
 *   
 *   const config = await fetchEntitlementsConfig();
 *   const eliteLounge = config.lounge_minutes_per_day.elite; // 60
 *   const label = perDayLabel(60); // "1 hr/day"
 */

const BACKEND_URL = window.location.origin;

let _cache = null;
let _cacheAt = 0;
const TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fallback config - matches backend defaults.
 * Used when API is unavailable (offline, loading, etc.)
 */
export const ENTITLEMENTS_FALLBACK = {
  lounge_minutes_per_day: {
    free: 20,
    inner_circle: 40,
    elite: 60,
    elite_plus: 120,
    admin: 999999,
  },
  speed_dating_minutes_per_month: {
    free: 30,
    inner_circle: 360,
    elite: 240,
    elite_plus: 240,  // SAME AS ELITE (not unlimited!)
    admin: 99999,
  },
  ticketed_event: {
    ticket_minutes_allowance: 60,
    isolated_from_plan_minutes: true,
    vip_extra_minutes: 0,
  },
  labels: {
    lounge: {
      free: "20 min/day",
      inner_circle: "40 min/day",
      elite: "60 min/day",
      elite_plus: "120 min/day",
    },
    speed_dating: {
      free: "30 min/month",
      inner_circle: "360 min/month",
      elite: "240 min/month",
      elite_plus: "240 min/month",
    },
  },
  version: "fallback",
};

/**
 * Fetch entitlements config from backend.
 * Caches result with TTL to avoid repeated calls.
 * Falls back to ENTITLEMENTS_FALLBACK on error.
 * 
 * @param {Object} options
 * @param {boolean} options.force - Force refresh ignoring cache
 * @returns {Promise<Object>} Entitlements config
 */
export async function fetchEntitlementsConfig({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cache && (now - _cacheAt) < TTL_MS) {
    return _cache;
  }

  try {
    const res = await fetch(`${BACKEND_URL}/api/entitlements/config`);
    if (!res.ok) {
      throw new Error(`entitlements_config_bad_status_${res.status}`);
    }
    const data = await res.json();
    _cache = data;
    _cacheAt = now;
    return data;
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[entitlementsConfig] using fallback:", e?.message);
    }
    _cache = ENTITLEMENTS_FALLBACK;
    _cacheAt = now;
    return ENTITLEMENTS_FALLBACK;
  }
}

/**
 * Convert minutes to human-readable format.
 * @param {number} minutes
 * @returns {string}
 */
export function minutesToHuman(minutes) {
  if (minutes == null) return "";
  if (minutes >= 99999) return "Admin";
  
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  
  if (h <= 0) return `${m} min`;
  if (m === 0) return h === 1 ? "1 hr" : `${h} hrs`;
  return `${h} hr ${m} min`;
}

/**
 * Format minutes as daily allowance label.
 * @param {number} mins
 * @returns {string}
 */
export function perDayLabel(mins) {
  if (mins == null) return "";
  if (mins >= 99999) return "Admin";
  return `${minutesToHuman(mins)}/day`;
}

/**
 * Format minutes as monthly allowance label.
 * @param {number} mins
 * @returns {string}
 */
export function perMonthLabel(mins) {
  if (mins == null) return "";
  if (mins >= 99999) return "Admin";
  return `${minutesToHuman(mins)}/month`;
}

/**
 * Get tier key from user object.
 * @param {Object} user
 * @returns {string} 'admin' | 'inner_circle' | 'elite_plus' | 'elite' | 'free'
 */
export function getUserTier(user) {
  if (!user) return "free";
  if (user.is_admin) return "admin";
  
  const ent = user.entitlements || {};
  
  // Check Inner Circle
  const ic = ent.inner_circle;
  if (ic === true || (ic && ic.active)) return "inner_circle";
  
  // Check Elite+
  const ep = ent.elite_plus;
  if (ep === true || (ep && ep.active)) return "elite_plus";
  
  // Check Elite
  const e = ent.elite;
  if (e === true || (e && e.active)) return "elite";
  
  return "free";
}

/**
 * Get lounge label for a user based on their tier.
 * @param {Object} config - Entitlements config
 * @param {Object} user - User object
 * @returns {string}
 */
export function getLoungeLabel(config, user) {
  const tier = getUserTier(user);
  const mins = config?.lounge_minutes_per_day?.[tier];
  return perDayLabel(mins);
}

/**
 * Get speed dating label for a user based on their tier.
 * @param {Object} config - Entitlements config
 * @param {Object} user - User object
 * @returns {string}
 */
export function getSpeedDatingLabel(config, user) {
  const tier = getUserTier(user);
  const mins = config?.speed_dating_minutes_per_month?.[tier];
  return perMonthLabel(mins);
}

export default {
  fetchEntitlementsConfig,
  ENTITLEMENTS_FALLBACK,
  minutesToHuman,
  perDayLabel,
  perMonthLabel,
  getUserTier,
  getLoungeLabel,
  getSpeedDatingLabel,
};
